import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const asList = (data) => (Array.isArray(data) ? data : data?.items || []);

// Searchable combobox for picking a customer/supplier by company name.
// Queries the server (`/customers` or `/suppliers`, which already support
// `?search=`) instead of holding every record in memory — needed once the
// list runs into the hundreds.
export function EntitySearchSelect({
  endpoint,
  value,
  label,
  onSelect,
  onClear,
  placeholder = "Search…",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    setTimeout(() => inputRef.current?.focus(), 0);
    const onDown = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onDown);
    return () => document.removeEventListener("keydown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(endpoint, { params: { page: 1, page_size: 20, search: query.trim() } })
        .then((r) => { if (!cancelled) setResults(asList(r.data)); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, endpoint]);

  return (
    <div ref={containerRef} style={{ position: "relative" }} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <button
        type="button"
        className="input-base flex items-center justify-between"
        style={{ textAlign: "left", width: "100%" }}
        data-testid={testId}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        <span style={{ color: value ? "inherit" : "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value ? (label || "—") : placeholder}
        </span>
        <ChevronsUpDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "var(--surface-1, #fff)", border: "1px solid var(--border, #e2e2e2)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 9999, overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border, #e2e2e2)", position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="input-base"
              style={{ padding: "6px 10px 6px 30px", fontSize: 13 }}
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {value && (
              <button
                type="button"
                className="btn-ghost"
                style={{ width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13, color: "var(--text-2)", display: "block" }}
                onClick={() => { onClear(); setOpen(false); }}
              >
                — Clear —
              </button>
            )}
            {loading ? (
              <div style={{ padding: 16, textAlign: "center", color: "var(--text-2)" }}>
                <Loader2 size={14} className="spin" />
              </div>
            ) : results.length === 0 ? (
              <div style={{ padding: "12px", fontSize: 13, color: "var(--text-2)", textAlign: "center" }}>
                {query.trim() ? "No match found." : "No records yet."}
              </div>
            ) : (
              results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="btn-ghost"
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13, display: "block",
                    background: item.id === value ? "var(--surface-2, #f5f5f5)" : undefined,
                  }}
                  onClick={() => { onSelect(item); setOpen(false); }}
                  data-testid={testId ? `${testId}-option-${item.id}` : undefined}
                >
                  <div style={{ fontWeight: 600 }}>{item.company}</div>
                  {(item.country || item.contact) && (
                    <div style={{ fontSize: 11, color: "var(--text-2)" }}>
                      {[item.contact, item.country].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EntitySearchSelect;
